/* eslint-disable jsx-a11y/no-autofocus */

import React from 'react'
import styled from 'styled-components'
import { Button, Flex, GenericBanner, Text, getPaletteColor } from 'pcln-design-system'
import ChildrenLineItem from '../ChildrenLineItem'
import TravelerLineItem from '../TravelerLineItem'
import Divider from '../Divider'
import { DEFAULT_MAX_ROOMS, ROOM_SIZE } from '../constants'
import { ITravelerSelectionContent } from '../types'
import { ArrowRight as ArrowRightIcon, Bed as BedIcon } from 'pcln-icons'

const MaxRoomsMessageText = styled(Text)`
  &&&:hover {
    color: ${getPaletteColor('primary.dark')};
    text-decoration: underline;
  }
`

const PopoverContent = styled(Flex)`
  flex-direction: column;
  font-family: ${(props) => props.theme.font};
`

function TravelerSelectionContent({
  ageSelectEnabled,
  autoFocus,
  className,
  disclaimer,
  isChildAgeSeti,
  isPopover,
  maxAdults,
  maxChildren,
  maxRooms: defaultMaxRooms = DEFAULT_MAX_ROOMS,
  maxRoomsMessage,
  maxRoomsUrl,
  roomsEnabled,
  state,
  handleClose,
  subLabelMap,
  lapInfantLabel,
  shouldCountLapInfant,
}: ITravelerSelectionContent) {
  const maxRooms =
    ageSelectEnabled && roomsEnabled && maxAdults && maxChildren
      ? Math.ceil((maxAdults + maxChildren) / ROOM_SIZE)
      : defaultMaxRooms

  const defaultMaxRoomsMessage = `You may only book up to ${maxRooms} rooms.`
  const hasCustomMaxRoomsMessage = maxRoomsMessage && maxRoomsUrl

  const finalMaxLapInfant = shouldCountLapInfant
    ? Math.min(state.adults, state.lapInfants + state.remainingTravelerCount)
    : state.adults
  const finalMaxChildren = maxChildren
    ? Math.min(maxChildren, state.children + state.remainingTravelerCount)
    : state.children + state.remainingTravelerCount

  return (
    <PopoverContent className={className} px={3}>
      {roomsEnabled && (
        <>
          <TravelerLineItem
            autoFocus={autoFocus}
            label='Rooms'
            min={1}
            max={maxRooms}
            count={state.rooms}
            onChange={state.setRooms}
          />
          {state.rooms === maxRooms && (
            <GenericBanner
              borderRadius='lg'
              color='caution.light'
              ctaText={
                hasCustomMaxRoomsMessage && (
                  <MaxRoomsMessageText color='primary.base'>
                    {' '}
                    <Flex alignItems='center' mt={2}>
                      {maxRooms + 1} Rooms or More <ArrowRightIcon size={18} ml={1} />
                    </Flex>
                  </MaxRoomsMessageText>
                )
              }
              fontSize={0}
              iconLeft={<BedIcon />}
              justifyContent='flex-start'
              px={2}
              py={2}
              mb={3}
              text={<Text>{maxRoomsMessage || defaultMaxRoomsMessage}</Text>}
              URLProps={
                hasCustomMaxRoomsMessage && {
                  href: maxRoomsUrl,
                  target: '_blank',
                }
              }
            />
          )}
          <Divider />
        </>
      )}
      <TravelerLineItem
        autoFocus={autoFocus && !roomsEnabled}
        label='Adults'
        subLabel={subLabelMap?.adults || ''}
        min={1}
        max={maxAdults || state.adults + state.remainingTravelerCount}
        count={state.adults}
        onChange={state.setAdults}
      />
      <Divider />
      <ChildrenLineItem
        ageSelectEnabled={ageSelectEnabled}
        max={finalMaxChildren}
        count={state.children}
        ages={state.childrenAges}
        onChange={state.setChildren}
        onAgeChange={state.setChildrenAges}
        isChildAgeSeti={isChildAgeSeti}
        subLabel={subLabelMap?.children || 'Ages 2 to 11'}
      />
      {!ageSelectEnabled && (
        <>
          <Divider />
          <TravelerLineItem
            label={lapInfantLabel || 'Infants on Lap'}
            subLabel={subLabelMap?.lapInfants || 'Under 2'}
            min={0}
            max={finalMaxLapInfant}
            count={state.lapInfants}
            onChange={state.setLapInfants}
          />
        </>
      )}
      {disclaimer && (
        <Text color='text.light' fontSize={0} mb={3} mt={2}>
          {disclaimer}
        </Text>
      )}
      {roomsEnabled && ageSelectEnabled && (
        <Text color='text.light' fontSize={0} mb={3} mt={2}>
          Add your child&apos;s age at check-in for the best deals and assistance. Each hotel has unique
          policies.
        </Text>
      )}
      {isPopover && (
        <Flex justifyContent='flex-end'>
          <Button size='medium' onClick={handleClose} mb={3}>
            Done
          </Button>
        </Flex>
      )}
    </PopoverContent>
  )
}

export default TravelerSelectionContent
